import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard'
import { adminApi } from '@/utils/api'
import type { AdminUser, AdminOrganizationStat } from '@/types/admin'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'

export default function AdminUsersPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [addOpen, setAddOpen] = useState(false)
  const [addForm, setAddForm] = useState({
    email: '',
    phoneNumber: '',
    password: '',
    firstName: '',
    lastName: '',
    notes: '',
  })
  const [addError, setAddError] = useState<string | null>(null)
  const [addSuccess, setAddSuccess] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [activityOpen, setActivityOpen] = useState(false)
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityError, setActivityError] = useState<string | null>(null)
  const [activity, setActivity] = useState<unknown[]>([])
  const [activityUserName, setActivityUserName] = useState<string>('')
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<string>('All')
  const [orgNames, setOrgNames] = useState<Record<string, string>>({})

  useEffect(() => {
    const fetchOrgNames = async () => {
      try {
        const res = await adminApi.organizationStats()
        const data =
          (res as unknown as { data?: AdminOrganizationStat[] }).data ||
          (res as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
          []
        const map: Record<string, string> = {}
        if (Array.isArray(data)) {
          for (const org of data) {
            if (org.id && org.name) map[org.id] = org.name
          }
        }
        setOrgNames(map)
      } catch {
        // Non-critical, org names just won't resolve
      }
    }
    void fetchOrgNames()
  }, [])

  const fetchUsers = async (filter?: string) => {
      try {
        setError(null)
        const params: { page: number; limit: number; role?: string; status?: string } = { page: 1, limit: 100 }

        // Apply filter
        const currentFilter = filter || activeFilter
        if (currentFilter !== 'All') {
          if (currentFilter === 'Active') {
            params.status = 'active'
          } else if (currentFilter === 'Inactive') {
            params.status = 'inactive'
          } else {
            // It's a role filter
            params.role = currentFilter
          }
        }

        const res = await adminApi.users(params)
        const payload = res as unknown as { data?: { data?: AdminUser[]; pagination?: { total?: number } } }
        const data = payload.data?.data || []
        setUsers(Array.isArray(data) ? data : [])
        setTotalCount(payload.data?.pagination?.total ?? data.length)
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load users')
      }
  }

  useEffect(() => {
    void fetchUsers()
  }, [activeFilter])

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    setAddError(null)
    setAddSuccess(null)
    setAdding(true)
    try {
      await adminApi.createAdmin({
        email: addForm.email,
        phoneNumber: addForm.phoneNumber,
        password: addForm.password,
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        ...(addForm.notes && { notes: addForm.notes }),
      })
      setAddSuccess('Admin created successfully.')
      setAddForm({
        email: '',
        phoneNumber: '',
        password: '',
        firstName: '',
        lastName: '',
        notes: '',
      })
      setAddOpen(false)
      await fetchUsers()
    } catch (err) {
      setAddError((err as Error)?.message || 'Failed to create admin')
    } finally {
      setAdding(false)
    }
  }

  const handleViewActivity = async (user: AdminUser) => {
    setActivityUserName(
      [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'User Activity'
    )
    setActivityOpen(true)
    setActivityError(null)
    setActivityLoading(true)
    try {
      const res = await adminApi.userActivity({ limit: 50 })
      const payload = res as { data?: { data?: unknown[] } | unknown[] }
      // Handle both nested { data: { data: [] } } and direct { data: [] } structures
      const activityData = payload.data
      if (Array.isArray(activityData)) {
        setActivity(activityData)
      } else if (activityData && typeof activityData === 'object' && 'data' in activityData) {
        setActivity(Array.isArray(activityData.data) ? activityData.data : [])
      } else {
        setActivity([])
      }
    } catch (err) {
      setActivityError((err as Error)?.message || 'Failed to load activity')
    } finally {
      setActivityLoading(false)
    }
  }

  const handleToggleStatus = async (user: AdminUser) => {
    if (updatingStatus) return // Prevent multiple simultaneous updates

    setUpdatingStatus(user.id)
    try {
      await adminApi.updateUserStatus(user.id, !user.isActive)
      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, isActive: !u.isActive } : u
        )
      )
      setTotalCount(prev => prev) // Recalculate if needed
    } catch (err) {
      setError((err as Error)?.message || 'Failed to update user status')
      // Optionally show a toast notification
    } finally {
      setUpdatingStatus(null)
    }
  }

  const filters = useMemo(
    () => ['All', 'Active', 'Inactive', 'super_admin', 'organization_admin', 'counselor', 'couple'],
    []
  )

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter)
  }

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      <div className='p-4 md:p-6 lg:p-8 space-y-6'>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8'>
          {/* Header */}
          <div className='flex items-center justify-between mb-6'>
            <div>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Users</h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>Manage platform users, status, and roles.</p>
            </div>
            <div className='flex items-center gap-3'>
              <span className='text-[24px] font-bold text-[#353535] hidden sm:block'>{userName}</span>
              <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-sm'>
                {userInitials}
              </div>
            </div>
          </div>

          {/* Stat Cards */}
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
            <StatCard
              title='Total Users'
              value={totalCount.toLocaleString()}
              subtitle='All roles'
              trend=''
            />
            <StatCard
              title='Active'
              value={users.filter((u) => u.isActive).length.toLocaleString()}
              subtitle='isActive=true'
              trend=''
            />
            <StatCard
              title='Inactive'
              value={users.filter((u) => !u.isActive).length.toLocaleString()}
              subtitle='isActive=false'
              trend=''
            />
          </div>

          {/* Users Table Section */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 space-y-4'>
            <h2 className='text-[18px] font-bold text-[#151515]'>User Management</h2>

            <div className='flex flex-wrap items-center justify-between gap-3'>
              <div className='flex flex-wrap gap-2'>
                {filters.map((f) => (
                  <button
                    key={f}
                    onClick={() => handleFilterClick(f)}
                    className={`px-3 py-2 rounded-[8px] text-xs font-semibold transition-colors ${
                      activeFilter === f
                        ? 'bg-[#151515] text-white'
                        : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <Sheet open={addOpen} onOpenChange={setAddOpen}>
                <SheetTrigger asChild>
                  <button className='px-4 py-2 rounded-[8px] bg-[#151515] hover:bg-[#2a2a2a] text-white text-sm font-bold'>
                    Add Admin
                  </button>
                </SheetTrigger>
                <SheetContent side='right' className='w-full sm:max-w-lg'>
                  <SheetHeader>
                    <SheetTitle>Add Admin</SheetTitle>
                  </SheetHeader>
                  <form className='mt-6 space-y-4' onSubmit={handleCreateAdmin}>
                    {addError && (
                      <div className='text-sm text-red-600 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2'>
                        {addError}
                      </div>
                    )}
                    {addSuccess && (
                      <div className='text-sm text-green-700 bg-green-50 border border-green-200 rounded-[8px] px-3 py-2'>
                        {addSuccess}
                      </div>
                    )}
                    <div className='space-y-1'>
                      <label className='text-xs font-semibold text-gray-700 text-start block'>First Name</label>
                      <input
                        className='w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] outline-none focus:ring-1 focus:ring-[#FF6600]'
                        required
                        value={addForm.firstName}
                        onChange={(e) => setAddForm((prev) => ({ ...prev, firstName: e.target.value }))}
                      />
                    </div>
                    <div className='space-y-1'>
                      <label className='text-xs font-semibold text-gray-700 text-start block'>Last Name</label>
                      <input
                        className='w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] outline-none focus:ring-1 focus:ring-[#FF6600]'
                        required
                        value={addForm.lastName}
                        onChange={(e) => setAddForm((prev) => ({ ...prev, lastName: e.target.value }))}
                      />
                    </div>
                    <div className='space-y-1'>
                      <label className='text-xs font-semibold text-gray-700 text-start block'>Email</label>
                      <input
                        type='email'
                        className='w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] outline-none focus:ring-1 focus:ring-[#FF6600]'
                        required
                        value={addForm.email}
                        onChange={(e) => setAddForm((prev) => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                    <div className='space-y-1'>
                      <label className='text-xs font-semibold text-gray-700 text-start block'>Phone Number</label>
                      <PhoneNumberInput
                        value={addForm.phoneNumber}
                        onChange={(value) => setAddForm((prev) => ({ ...prev, phoneNumber: value }))}
                        placeholder='Phone number'
                        required
                        containerClassName='border-gray-300'
                        inputClassName='py-0'
                      />
                    </div>
                    <div className='space-y-1'>
                      <label className='text-xs font-semibold text-gray-700 text-start block'>Password</label>
                      <input
                        type='password'
                        className='w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] outline-none focus:ring-1 focus:ring-[#FF6600]'
                        required
                        value={addForm.password}
                        onChange={(e) => setAddForm((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder='Min 8 characters'
                      />
                    </div>
                    <div className='space-y-1'>
                      <label className='text-xs font-semibold text-gray-700 text-start block'>Notes</label>
                      <textarea
                        className='w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] outline-none focus:ring-1 focus:ring-[#FF6600]'
                        value={addForm.notes}
                        onChange={(e) => setAddForm((prev) => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                      />
                    </div>
                    <div className='flex justify-end gap-2 pt-2'>
                      <Button type='button' variant='outline' onClick={() => setAddOpen(false)} disabled={adding}>
                        Cancel
                      </Button>
                      <Button type='submit' className='bg-[#151515] hover:bg-[#2a2a2a] font-bold rounded-[8px]' disabled={adding}>
                        {adding ? 'Creating...' : 'Create Admin'}
                      </Button>
                    </div>
                  </form>
                </SheetContent>
              </Sheet>
            </div>

            <div className='bg-[rgba(237,239,243,0.5)] rounded-[8px] overflow-hidden'>
              <div className='grid grid-cols-6 gap-2 px-4 py-2 text-start'>
                <span className='font-medium text-[14px] text-[#475467]'>User</span>
                <span className='font-medium text-[14px] text-[#475467]'>Email</span>
                <span className='font-medium text-[14px] text-[#475467]'>Role</span>
                <span className='font-medium text-[14px] text-[#475467]'>Status</span>
                <span className='font-medium text-[14px] text-[#475467]'>Organization</span>
                <span className='font-medium text-[14px] text-[#475467] text-right'>Actions</span>
              </div>
              {users.length === 0 ? (
                <div className='py-12 text-center'>
                  <p className='text-[14px] text-[#101828]'>No users found.</p>
                  <p className='text-xs text-[#475467] mt-1'>Try adjusting your filters or add new users.</p>
                </div>
              ) : (
                users.map((item) => {
                  const name = [item.firstName, item.lastName].filter(Boolean).join(' ') || item.email
                  const email = item.email
                  const role = item.role
                  const isActive = item.isActive
                  const orgId = item.organizationId || ''
                  const orgDisplay = orgId ? (orgNames[orgId] || orgId) : '—'
                  return (
                    <div
                      key={item.id}
                      className='grid grid-cols-6 gap-2 px-4 py-3 text-[14px] text-[#101828] border-b border-[#eaecf0] hover:bg-[#f9fafb] items-center text-start'
                    >
                      <span>{name}</span>
                      <span>{email}</span>
                      <span className='font-semibold text-[#101828]'>{role}</span>
                      <span className={isActive ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                      {orgId ? (
                        <button
                          className='text-left text-orange-600 hover:underline truncate text-[14px]'
                          onClick={() => navigate(`/admin/organizations/${orgId}`)}
                        >
                          {orgDisplay}
                        </button>
                      ) : (
                        <span>—</span>
                      )}
                      <div className='flex justify-end gap-2 text-xs'>
                        <button
                          className='px-3 py-1 rounded-[8px] bg-blue-50 text-blue-600 hover:bg-blue-100'
                          onClick={() => navigate(`/admin/users/${item.id}`)}
                        >
                          View
                        </button>
                        <button
                          className='px-3 py-1 rounded-[8px] bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                          onClick={() => handleToggleStatus(item)}
                          disabled={updatingStatus === item.id}
                        >
                          {updatingStatus === item.id
                            ? 'Updating...'
                            : !item.isActive
                            ? 'Reactivate'
                            : 'Deactivate'}
                        </button>
                        <button
                          className='px-3 py-1 rounded-[8px] bg-orange-50 text-orange-600 hover:bg-orange-100'
                          onClick={() => handleViewActivity(item)}
                        >
                          View Activity
                        </button>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            {error && <p className='text-sm text-red-600'>{error}</p>}
            <p className='text-xs text-[#475467]'>
              Table powered by admin user endpoints with status toggles and bulk actions.
            </p>
          </div>
        </div>
      </div>

      <Sheet open={activityOpen} onOpenChange={setActivityOpen}>
        <SheetContent side='right' className='w-full sm:max-w-xl'>
          <SheetHeader>
            <SheetTitle>{activityUserName || 'User Activity'}</SheetTitle>
          </SheetHeader>
          <div className='mt-4 space-y-3'>
            {activityError && (
              <div className='text-sm text-red-600 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2'>
                {activityError}
              </div>
            )}
            {activityLoading && <p className='text-[14px] text-[#475467]'>Loading activity...</p>}
            {!activityLoading && !activityError && (
              <div className='space-y-2 max-h-[70vh] overflow-y-auto'>
                {activity.length === 0 && <p className='text-[14px] text-[#475467]'>No activity found.</p>}
                {activity.map((entry, idx) => (
                  <div
                    key={idx}
                    className='rounded-[8px] border border-[#eaecf0] bg-[rgba(237,239,243,0.5)] px-3 py-2 text-[14px] text-[#101828]'
                  >
                    <pre className='text-xs whitespace-pre-wrap break-words'>{JSON.stringify(entry, null, 2)}</pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
